import React, {
  forwardRef, useImperativeHandle, useRef, useState,
} from 'react';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faLightbulb } from '@fortawesome/free-solid-svg-icons';
import sanitizeHtml from 'sanitize-html';
import { env } from '~/commons/config';
import { useFormikContext } from 'formik';
import { Modal } from 'react-bootstrap';

import FormCheckGroup from '~/commons/components/FormCheckGroup.jsx';
import NewLines from '~/commons/components/Newlines.jsx';
import { numToLetter } from '~/commons/helpers';
import Padding from '../layout/Padding.jsx';
import Html from '../../../commons/components/Html.jsx';

const FieldsQuestions = forwardRef(({
  questions,
}, ref) => {
  const { values } = useFormikContext();
  const qRefs = useRef({});
  const [openTip, setOpenTip] = useState(null);

  const sanitizeTips = (html) => sanitizeHtml(html || '', {
    allowedTags: sanitizeHtml.defaults.allowedTags.concat(['img', 'h1', 'h2', 'iframe']),
    allowedAttributes: {
      ...sanitizeHtml.defaults.allowedAttributes,
      a: ['href', 'name', 'target'],
      img: ['src', 'alt', 'width', 'height', 'style', 'loading'],
      iframe: ['src', 'width', 'height', 'frameborder', 'allow', 'allowfullscreen'],
    },
    transformTags: {
      img: (tagName, attribs) => {
        let src = attribs.src || '';
        const base = (env && env.BASE) || window.location.origin;
        if (src && !src.match(/^https?:\/\//i)) {
          if (src.startsWith('/')) {
            src = `${base}${src}`;
          } else {
            src = `${base}/${src}`;
          }
        }

        return {
          tagName: 'img',
          attribs: {
            ...attribs,
            src,
            loading: 'lazy',
          },
        };
      },
      iframe: (tagName, attribs) => {
        let src = attribs.src || '';
        const base = (env && env.BASE) || window.location.origin;
        if (src && !src.match(/^https?:\/\//i)) {
          if (src.startsWith('/')) {
            src = `${base}${src}`;
          } else {
            src = `${base}/${src}`;
          }
        }
        return {
          tagName: 'iframe',
          attribs: {
            ...attribs,
            src,
            allowFullScreen: attribs.allowfullscreen || attribs.allowFullScreen,
          },
        };
      },
    },
  });

  useImperativeHandle(ref, () => ({
    scrollIntoView: (_id) => {
      qRefs.current[_id].scrollIntoView({ block: 'center' });
    },
  }));

  return (
    <>
      <Padding className="readable">
      {questions.map((q, i) => {
        const { answered } = values.answers[i];

        return (
          <div
            key={q._id}
            className="card my-4"
            ref={(el) => { qRefs.current[q._id] = el; }}
          >
            <div className="card-body">
              <div className="row text-secondary" style={{ position: 'relative' }}>
                <h5 className="col-auto pr-0">
                  {i + 1}.
                </h5>
                <h5 className="col pl-2">
                  <Html>{q.question}</Html>
                </h5>
                {q.tips && (
                  <div className="col-auto d-flex align-items-start justify-content-end pr-0">
                    <button
                      type="button"
                      className="btn btn-link p-1"
                      onClick={(ev) => {
                        ev.stopPropagation();
                        setOpenTip(openTip === q._id ? null : q._id);
                      }}
                      aria-label="Show tip"
                      style={{ color: '#f5c518' }}
                    >
                      <FA icon={faLightbulb} />
                    </button>
                  </div>
                )}
              </div>
              {q.img && (
                <div className="py-3 text-center">
                  <img src={q.img} />
                </div>
              )}
              {q.choices.map(({ c, ii }, jj) => (
                <div key={ii} className="mb-2">
                  <FormCheckGroup
                    className="mb-2"
                    name={`answers.${i}.answered`}
                    type="radio"
                    label={`${numToLetter(jj, true)}. ${c}`}
                    value={ii.toString()}
                  />
                </div>
              ))}
              {/* answered */}
              {answered !== undefined && (
                <div className={`
                  bg-quinary
                  mt-3 py-2 px-4 position-relative
                `}>
                  我的答案:
                  &nbsp;
                  {numToLetter(
                    q.choices.indexOf(
                      q.choices.find(({ ii }) => String(ii) === String(answered)),
                    ),
                    true,
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </Padding>
    {(() => {
      const selected = openTip ? questions.find((qq) => qq._id === openTip) : null;

      return (
        <Modal
          show={!!selected}
          onHide={() => setOpenTip(null)}
          centered
          size="md"
        >
          <Modal.Header closeButton>
            <Modal.Title>提示</Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {selected && (
              <div dangerouslySetInnerHTML={{ __html: sanitizeTips(selected.tips || '') }} />
            )}
          </Modal.Body>
        </Modal>
      );
    })()}
  </>
  );
});

FieldsQuestions.displayName = 'FieldsQuestions';

export default FieldsQuestions;
